import {
  Component, OnInit, EventEmitter, ElementRef, Input, Output, DoCheck, OnDestroy,
  ViewContainerRef, ViewChild, ComponentRef, Renderer2, ComponentFactoryResolver,
} from '@angular/core';
import { FormGroup, FormArray } from '@angular/forms';
import { FormlyConfig, TypeOption } from '../services/formly.config';
import { Field } from '../templates/field';
import { evalExpression } from '../utils';
import { FormlyFieldConfig, FormlyOptions, FormlyValueChangeEvent } from './formly.field.config';
import { Subscription } from 'rxjs/Subscription';
import { debounceTime } from 'rxjs/operator/debounceTime';
import { map } from 'rxjs/operator/map';
export class FormlyField implements DoCheck, OnInit, OnDestroy {
   model: any;
   form: FormGroup;
   field: FormlyFieldConfig;
   options: FormlyOptions = {};
   modelChange: EventEmitter<any> = new EventEmitter();
   fieldComponent: ViewContainerRef;
private componentRefs: ComponentRef<Field>[] = [];
private _subscriptions: Subscription[] = [];
/**
 * @param {?} elementRef
 * @param {?} renderer
 * @param {?} formlyConfig
 * @param {?} componentFactoryResolver
 */
constructor(
private elementRef: ElementRef,
private renderer: Renderer2,
private formlyConfig: FormlyConfig,
private componentFactoryResolver: ComponentFactoryResolver,
  ) {}
/**
 * @return {?}
 */
ngDoCheck() {
    this.checkExpressionChange();
    this.checkVisibilityChange();
  }
/**
 * @return {?}
 */
ngOnInit() {
    this.createFieldComponents();
    if (this.field.hide === true) {
      this.toggleHide(true);
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this.componentRefs.map(componentRef => componentRef.destroy());
    this._subscriptions.map(subscriber => subscriber.unsubscribe());
    this._subscriptions = this.componentRefs = [];
  }
/**
 * @return {?}
 */
private createFieldComponents() {
    if (this.field && !this.field.template && !this.field.fieldGroup && !this.field.fieldArray) {
      let /** @type {?} */ debounce = 0;
      if (this.field.modelOptions && this.field.modelOptions.debounce && this.field.modelOptions.debounce.default) {
        debounce = this.field.modelOptions.debounce.default;
      }

      const /** @type {?} */ fieldComponentRef = this.createFieldComponent();
      if (this.field.key) {
        let /** @type {?} */ valueChanges = fieldComponentRef.instance.formControl.valueChanges;
        if (debounce > 0) {
          valueChanges = debounceTime.call(valueChanges, debounce);
        }
        if (this.field.parsers && this.field.parsers.length > 0) {
          this.field.parsers.map(parserFn => {
            valueChanges = map.call(valueChanges, parserFn);
          });
        }

        this._subscriptions.push(valueChanges.subscribe((event) =>
          this.modelChange.emit({ key: this.field.key, value: event }),
        ));
      }
    } else if (this.field.fieldGroup || this.field.fieldArray) {
      this.createFieldComponent();
    }
  }
/**
 * @return {?}
 */
private createFieldComponent(): ComponentRef<Field> {
    if (this.field.fieldGroup) {
      this.field.type = this.field.type || 'formly-group';
    }
    const /** @type {?} */ type = this.formlyConfig.getType(this.field.type), /** @type {?} */
      wrappers = this.getFieldWrappers(type);

    let /** @type {?} */ fieldComponent = this.fieldComponent;
    wrappers.map(wrapperName => {
      let /** @type {?} */ wrapperRef = this.createComponent(fieldComponent, this.formlyConfig.getWrapper(wrapperName).component);
      fieldComponent = wrapperRef.instance.fieldComponent;
    });

    return this.createComponent(fieldComponent, type.component);
  }
/**
 * @param {?} type
 * @return {?}
 */
private getFieldWrappers(type: TypeOption) {
    let /** @type {?} */ templateManipulators = {
      preWrapper: [],
      postWrapper: [],
    };

    if (this.field.templateOptions) {
      this.mergeTemplateManipulators(templateManipulators, this.field.templateOptions.templateManipulators);
    }

    this.mergeTemplateManipulators(templateManipulators, this.formlyConfig.templateManipulators);

    let /** @type {?} */ preWrappers = templateManipulators.preWrapper.map(m => m(this.field)).filter(type => type), /** @type {?} */
      postWrappers = templateManipulators.postWrapper.map(m => m(this.field)).filter(type => type);

    if (!this.field.wrappers) this.field.wrappers = [];
    if (!type.wrappers) type.wrappers = [];

    return [...preWrappers, ...this.field.wrappers, ...postWrappers];
  }
/**
 * @param {?} source
 * @param {?} target
 * @return {?}
 */
private mergeTemplateManipulators(source, target) {
    target = target || {};
    if (target.preWrapper) {
      source.preWrapper = source.preWrapper.concat(target.preWrapper);
    }
    if (target.postWrapper) {
      source.postWrapper = source.postWrapper.concat(target.postWrapper);
    }

    return source;
  }
/**
 * @param {?} fieldComponent
 * @param {?} component
 * @return {?}
 */
private createComponent(fieldComponent: ViewContainerRef, component: any): ComponentRef<any> {
    let /** @type {?} */ componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
    let /** @type {?} */ ref = /** @type {?} */(( <ComponentRef<Field>>fieldComponent.createComponent(componentFactory)));

    Object.assign(ref.instance, {
        model: this.model,
        form: this.form,
        field: this.field,
        options: this.options,
    });

    this.componentRefs.push(ref);

    return ref;
  }
/**
 * @return {?}
 */
private checkVisibilityChange() {
    if (this.field && this.field.hideExpression) {
      const /** @type {?} */ hideExpressionResult: boolean = !!evalExpression(
        this.field.hideExpression,
        this,
        [this.model, this.options.formState],
      );

      if (hideExpressionResult !== this.field.hide) {
        this.toggleHide(hideExpressionResult);
      }
    }
  }
/**
 * @return {?}
 */
private checkExpressionChange() {
    if (this.field && this.field.expressionProperties) {
      const /** @type {?} */ expressionProperties = this.field.expressionProperties;

      for (let /** @type {?} */ key in expressionProperties) {
        const /** @type {?} */ expressionValue = evalExpression(
          expressionProperties[key].expression,
          this,
          [this.model, this.options.formState],
        );

        if (expressionProperties[key].expressionValue !== expressionValue) {
          expressionProperties[key].expressionValue = expressionValue;
          evalExpression(
            expressionProperties[key].expressionValueSetter,
            this,
            [expressionValue, this.model, this.field.templateOptions, this.field.validation],
          );
        }
      }

      const /** @type {?} */ formControl = this.field.formControl;
      if (formControl) {
        if (formControl.status === 'DISABLED' && !this.field.templateOptions.disabled) {
            formControl.enable();
        }
        if (formControl.status !== 'DISABLED' && this.field.templateOptions.disabled) {
            formControl.disable();
        }
      }
    }
  }
/**
 * @param {?} value
 * @return {?}
 */
private toggleHide(value: boolean) {
    this.field.hide = value;
    if (this.field.formControl) {
      if (value === true && this.form.get(this.field.key)) {
        setTimeout(() => this.removeFieldControl());
      } else if (value === false && !this.form.get(this.field.key)) {
        setTimeout(() => this.addFieldControl());
      }
    }

    this.renderer.setStyle(this.elementRef.nativeElement, 'display', value ? 'none' : '');

    this.field.templateOptions.hidden = value;
    if (this.options.fieldChanges) {
      this.options.fieldChanges.next( /** @type {?} */((<FormlyValueChangeEvent> { field: this.field, type: 'hidden', value })));
    }
  }
/**
 * @return {?}
 */
private get fieldKey() {
    return this.field.key.split('.').pop();
  }
/**
 * @return {?}
 */
private get fieldParentFormControl(): FormArray|FormGroup {
      const /** @type {?} */ paths = this.field.key.split('.');
      paths.pop(); // remove last path

      return /** @type {?} */(( (paths.length > 0 ? this.form.get(paths) : this.form) as any));
  }
/**
 * @return {?}
 */
private addFieldControl() {
    const /** @type {?} */ parent = this.fieldParentFormControl;

    if (parent instanceof FormArray) {
      parent.push(this.field.formControl);
    } else if (parent instanceof FormGroup) {
      parent.addControl(this.fieldKey, this.field.formControl);
    }
  }
/**
 * @return {?}
 */
private removeFieldControl() {
    const /** @type {?} */ parent = this.fieldParentFormControl;

    if (parent instanceof FormArray) {
      parent.removeAt( /** @type {?} */((this.fieldKey as any)));
    } else if (parent instanceof FormGroup) {
      parent.removeControl(this.fieldKey);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'formly-field',
  template: `
    <ng-template #fieldComponent></ng-template>
    <div *ngIf="field.template && !field.fieldGroup" [innerHtml]="field.template"></div>
  `,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: Renderer2, },
{type: FormlyConfig, },
{type: ComponentFactoryResolver, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'model': [{ type: Input },],
'form': [{ type: Input },],
'field': [{ type: Input },],
'options': [{ type: Input },],
'modelChange': [{ type: Output },],
'fieldComponent': [{ type: ViewChild, args: ['fieldComponent', {read: ViewContainerRef}, ] },],
};
}

function FormlyField_tsickle_Closure_declarations() {
/** @type {?} */
FormlyField.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FormlyField.ctorParameters;
/** @type {?} */
FormlyField.propDecorators;
/** @type {?} */
FormlyField.prototype.model;
/** @type {?} */
FormlyField.prototype.form;
/** @type {?} */
FormlyField.prototype.field;
/** @type {?} */
FormlyField.prototype.options;
/** @type {?} */
FormlyField.prototype.modelChange;
/** @type {?} */
FormlyField.prototype.fieldComponent;
/** @type {?} */
FormlyField.prototype.componentRefs;
/** @type {?} */
FormlyField.prototype._subscriptions;
/** @type {?} */
FormlyField.prototype.elementRef;
/** @type {?} */
FormlyField.prototype.renderer;
/** @type {?} */
FormlyField.prototype.formlyConfig;
/** @type {?} */
FormlyField.prototype.componentFactoryResolver;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
